import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, emailNote } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loading, StatusBadge, Detail, ErrorBox } from "@/components/Common";
import { SlotPicker } from "@/components/SlotPicker";

const ADMIN_CFG = { working_days: [0, 1, 2, 3, 4, 5, 6], unavailable_dates: [], max_advance_days: 365 };

export function AppointmentDialog({ id, onClose, onChanged }) {
  const [a, setA] = useState(null);
  const [cfg, setCfg] = useState(ADMIN_CFG);
  const [mode, setMode] = useState(null);
  const [slot, setSlot] = useState({ date: null, start: null });
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const slotsUrl = useCallback((d) => `/admin/availability/slots?date=${d}&exclude_id=${id}`, [id]);

  useEffect(() => {
    if (!id) return;
    setA(null); setMode(null); setMsg(""); setErr(null);
    api.get(`/admin/appointments/${id}`).then((r) => setA(r.data)).catch((e) => setErr(errMsg(e)));
    api.get("/availability/config").then((r) => setCfg({ ...r.data, max_advance_days: 365 })).catch(() => {});
  }, [id]);

  const run = async (action) => {
    setBusy(true); setErr(null);
    try {
      const body = { action, message: msg, ...(["suggest", "reschedule"].includes(action) ? slot : {}) };
      const r = await api.post(`/admin/appointments/${id}/action`, body);
      toast.success(`Appointment ${r.data.status.toLowerCase()}. ${emailNote(r.data.email_status)}`);
      onChanged?.();
      onClose();
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  const s = a?.status;
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto bg-card" data-testid="appt-dialog">
        <DialogTitle>Appointment details</DialogTitle>
        <DialogDescription className="sr-only">Review and manage this appointment</DialogDescription>
        {!a ? (err ? <ErrorBox error={err} /> : <Loading />) : (
          <div className="space-y-4">
            {s === "Requested" && <p className="rounded-md border border-amber-200 bg-amber-50 p-2 text-sm text-amber-900" data-testid="appt-pending-note">Pending - requested by the customer and NOT yet confirmed.</p>}
            <dl className="grid gap-3 sm:grid-cols-2">
              <Detail label="Status"><StatusBadge status={s} testId="appt-dialog-status" /></Detail>
              <Detail label="Type">{a.type}</Detail>
              <Detail label="Date and time">{fmtDate(a.date)} {a.start}–{a.end}</Detail>
              <Detail label="Job request"><Link className="text-primary underline" to={`/admin/requests/${a.request_id}`}>{a.request_reference}</Link></Detail>
              <Detail label="Customer">{a.customer?.name}</Detail>
              <Detail label="Contact">{a.customer?.email}<br />{a.customer?.phone}</Detail>
              <Detail label="Property">{a.property ? `${a.property.address}, ${a.property.postcode}` : ""}</Detail>
              <Detail label="Customer notes">{a.customer_notes}</Detail>
              {a.suggested && <Detail label="Suggested time">{fmtDate(a.suggested.date)} {a.suggested.start} (awaiting customer)</Detail>}
            </dl>
            <ErrorBox error={err} />
            {mode ? (
              <div className="space-y-3 rounded-md border border-border p-3">
                <p className="text-sm font-medium">{mode === "suggest" ? "Suggest another time to the customer" : "Reschedule (will be confirmed)"}</p>
                <SlotPicker admin config={cfg} slotsUrl={slotsUrl} value={slot} onChange={setSlot} />
                <Textarea aria-label="Message to customer" placeholder="Optional message to the customer" value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="appt-message-input" />
                <div className="flex gap-2">
                  <Button onClick={() => run(mode)} disabled={busy || !slot.start} data-testid="appt-mode-submit-btn">Save</Button>
                  <Button variant="ghost" onClick={() => setMode(null)}>Back</Button>
                </div>
              </div>
            ) : (
              <>
                <Textarea aria-label="Message to customer" placeholder="Optional message to the customer" value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="appt-message-input" />
                <div className="flex flex-wrap gap-2">
                  {s === "Requested" && <Button onClick={() => run("confirm")} disabled={busy} data-testid="appt-confirm-btn">Confirm</Button>}
                  {["Requested", "Rescheduling Requested"].includes(s) && <Button variant="outline" onClick={() => run("decline")} disabled={busy} data-testid="appt-decline-btn">Decline</Button>}
                  {["Requested", "Confirmed"].includes(s) && <Button variant="outline" onClick={() => setMode("suggest")} data-testid="appt-suggest-btn">Suggest another time</Button>}
                  {["Requested", "Confirmed", "Rescheduling Requested"].includes(s) && <Button variant="outline" onClick={() => setMode("reschedule")} data-testid="appt-reschedule-btn">Reschedule</Button>}
                  {s === "Confirmed" && <Button variant="secondary" onClick={() => run("complete")} disabled={busy} data-testid="appt-complete-btn">Mark completed</Button>}
                  {["Requested", "Confirmed", "Rescheduling Requested"].includes(s) && <Button variant="destructive" onClick={() => window.confirm("Cancel this appointment?") && run("cancel")} disabled={busy} data-testid="appt-cancel-admin-btn">Cancel</Button>}
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
